/**
 * The v2 lexicon — v2's nine platform tables ported ONCE, as data
 * (ADR-004 §5: the tables' knowledge is preserved as priors; their detector
 * ordering and skip-pattern code are NOT carried). Source of truth for the
 * extraction: blackBoxIKAnimator/src/retargeting/BoneMapper.js (read-only
 * harvest, 2026-07-12) — platform tables at initializePlatformMappings(),
 * fuzzy aliases at initializeStandardBones().
 *
 * Keys are BBSA ontology IDs (the v2 standard-bone ids translated during
 * extraction: leftUpperArm→LeftArm, leftLowerLeg→LeftLeg, little→Pinky,
 * chest→Spine2, pelvis→Hips). Platform notes: ready_player_me DEPRECATED
 * (Netflix acquisition, 2026-07-11) and opensim DEPRIORITIZED (licensing) —
 * their name knowledge is kept, they are never corpus targets and carry no
 * special weight. The ontology IDs themselves are NOT listed — self-match
 * is ontology-driven at scoring time, not lexicon data.
 * Depends on: ir/priors (types). Depended on by: kernel/mapping, app.
 */
import type { PriorsBundle, Synonym } from '../../ir/priors.js';

type Names = Record<string, string>;

const SIDES = [
  ['Left', 'Left'],
  ['Right', 'Right'],
] as const;

/** Expand a per-side table: `{SideToken}` in names, `{Side}` in BBSA ids. */
function sided(table: Record<string, string>, sideToken: (bbsa: string) => [string, string][]): Names {
  const out: Names = {};
  for (const [id, name] of Object.entries(table)) {
    for (const [bbsaSide, tok] of sideToken(id)) {
      out[id.replace('{Side}', bbsaSide)] = name.replace('{S}', tok);
    }
  }
  return out;
}

function bothSides(table: Record<string, string>, leftTok: string, rightTok: string): Names {
  return sided(table, () => [
    ['Left', leftTok],
    ['Right', rightTok],
  ]);
}

const FINGERS = ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'] as const;

/** {Side}Hand{Finger}{1..3} → name via a template. v2 mapped 3 segments; segment 4 (tips) was skipped. */
function fingerTable(template: (side: 'Left' | 'Right', finger: string, seg: number) => string): Names {
  const out: Names = {};
  for (const [side] of SIDES) {
    for (const finger of FINGERS) {
      for (let seg = 1; seg <= 3; seg += 1) {
        out[`${side}Hand${finger}${seg}`] = template(side, finger, seg);
      }
    }
  }
  return out;
}

// ---- Platform tables (BBSA-keyed; names verbatim from the v2 tables) ----

/** Mixamo: 'mixamorig' + the BBSA id, for every bone v2 mapped (incl. fingers). */
const MIXAMO: Names = (() => {
  const core = [
    'Hips', 'Spine', 'Spine1', 'Spine2', 'Neck', 'Head',
    ...SIDES.flatMap(([s]) => [
      `${s}Shoulder`, `${s}Arm`, `${s}ForeArm`, `${s}Hand`,
      `${s}UpLeg`, `${s}Leg`, `${s}Foot`, `${s}ToeBase`,
    ]),
  ];
  const out: Names = Object.fromEntries(core.map((id) => [id, `mixamorig${id}`]));
  Object.assign(out, fingerTable((side, finger, seg) => `mixamorig${side}Hand${finger}${seg}`));
  return out;
})();

/** Meshy3D: Mixamo-convention names, zero-padded spine numbers, lowercase neck. */
const MESHY3D: Names = {
  Hips: 'Hips', Spine: 'Spine', Spine1: 'Spine01', Spine2: 'Spine02', Neck: 'neck', Head: 'Head',
  ...bothSides(
    {
      '{Side}Shoulder': '{S}Shoulder', '{Side}Arm': '{S}Arm', '{Side}ForeArm': '{S}ForeArm', '{Side}Hand': '{S}Hand',
      '{Side}UpLeg': '{S}UpLeg', '{Side}Leg': '{S}Leg', '{Side}Foot': '{S}Foot', '{Side}ToeBase': '{S}ToeBase',
    },
    'Left', 'Right',
  ),
};

/** TripoAI: L_/R_ + anatomical part names; Hip/Pelvis/Waist axials; NeckTwist01 as neck. */
const TRIPOAI: Names = {
  Hips: 'Hip', Spine: 'Waist', Spine1: 'Spine01', Spine2: 'Spine02', Neck: 'NeckTwist01', Head: 'Head',
  ...bothSides(
    {
      '{Side}Shoulder': '{S}_Clavicle', '{Side}Arm': '{S}_Upperarm', '{Side}ForeArm': '{S}_Forearm', '{Side}Hand': '{S}_Hand',
      '{Side}UpLeg': '{S}_Thigh', '{Side}Leg': '{S}_Calf', '{Side}Foot': '{S}_Foot', '{Side}ToeBase': '{S}_ToeBase',
    },
    'L', 'R',
  ),
};

/**
 * TripoAI rig-preset fingers (2026-09-23, tiger-actorcore — Tripo's
 * "ActorCore" preset keeps the native part names above and adds three real
 * joints per digit, `Mid` for the middle finger, no end bone). Kept in their
 * own table so the v2 harvest above stays verbatim; the tip is synthesized.
 */
const TRIPO_PRESET_FINGERS: Names = fingerTable(
  (side, finger, seg) => `${side === 'Left' ? 'L' : 'R'}_${finger === 'Middle' ? 'Mid' : finger}${seg}`,
);

/**
 * Unreal Engine Mannequin (UE4/UE5) — NOT a v2 table: added 2026-09-23 when
 * Tripo's "UE5" rig preset arrived (tiger-ue5: pelvis → spine_01..03,
 * clavicle_l, upperarm_l, lowerarm_l, hand_l, thigh_l, calf_l, foot_l,
 * ball_l, thumb_01_l … pinky_03_l). Three-segment spines (UE4, the Tripo
 * preset) take spine_03 as the chest. The UE5 Manny's FIVE-segment spine
 * (clavicles and neck off spine_05) is deliberately NOT declared: a
 * spine_05 → Spine2 synonym would tie with spine_03 on the same rig (both
 * exact, equal score — Codex P2, #185), and the structural rule that breaks
 * the tie (the chest is the spine the clavicles hang from) waits for a
 * five-spine Manny specimen to measure it on. Until then such a rig maps
 * spine_03 as the chest by name, a known gap rather than a guess. The
 * *_twist_01 supplements have no BBSA slot and stay unmapped by design, like
 * Tripo's own twist supplements. `root` is the ground bone, not the pelvis.
 */
const UNREAL_MANNEQUIN: Names = {
  Hips: 'pelvis', Spine: 'spine_01', Spine1: 'spine_02', Spine2: 'spine_03', Neck: 'neck_01', Head: 'head',
  ...bothSides(
    {
      '{Side}Shoulder': 'clavicle_{S}', '{Side}Arm': 'upperarm_{S}', '{Side}ForeArm': 'lowerarm_{S}', '{Side}Hand': 'hand_{S}',
      '{Side}UpLeg': 'thigh_{S}', '{Side}Leg': 'calf_{S}', '{Side}Foot': 'foot_{S}', '{Side}ToeBase': 'ball_{S}',
    },
    'l', 'r',
  ),
  ...fingerTable((side, finger, seg) => `${finger.toLowerCase()}_0${seg}_${side === 'Left' ? 'l' : 'r'}`),
};

/**
 * Unity Humanoid (Mecanim) avatar names as Tripo's "Unity" rig preset writes
 * them (2026-09-23, tiger-unity): `Left_UpperArm`, `Left_LowerLeg`, digits as
 * Proximal/Intermediate/Distal + a `DistalEnd` tip, `Left_Toes`/`ToesEnd`.
 * Chest → Spine1 and UpperChest → Spine2, the same reading as the VRM table
 * (VRM's humanoid is Unity's). `Neck_Twist_A` has no slot.
 */
const UNITY_HUMANOID: Names = (() => {
  const seg = ['', 'Proximal', 'Intermediate', 'Distal', 'DistalEnd'] as const;
  const out: Names = {
    Hips: 'Hips', Spine: 'Spine', Spine1: 'Chest', Spine2: 'UpperChest', Neck: 'Neck', Head: 'Head',
    LeftEye: 'Left_Eye', RightEye: 'Right_Eye',
    ...bothSides(
      {
        '{Side}Shoulder': '{S}_Shoulder', '{Side}Arm': '{S}_UpperArm', '{Side}ForeArm': '{S}_LowerArm', '{Side}Hand': '{S}_Hand',
        '{Side}UpLeg': '{S}_UpperLeg', '{Side}Leg': '{S}_LowerLeg', '{Side}Foot': '{S}_Foot',
        '{Side}ToeBase': '{S}_Toes', '{Side}Toe_End': '{S}_ToesEnd',
      },
      'Left', 'Right',
    ),
  };
  for (const [side] of SIDES) {
    for (const finger of FINGERS) {
      for (let k = 1; k <= 4; k += 1) out[`${side}Hand${finger}${k}`] = `${side}_${finger}${seg[k]}`;
    }
  }
  return out;
})();

/** Character Creator: CC_Base_ prefix, Tripo-like part names, spine chain shifted. */
const CHARACTER_CREATOR: Names = {
  Hips: 'CC_Base_Hip', Spine: 'CC_Base_Spine01', Spine1: 'CC_Base_Spine02', Spine2: 'CC_Base_Spine03',
  Neck: 'CC_Base_NeckTwist01', Head: 'CC_Base_Head',
  ...bothSides(
    {
      '{Side}Shoulder': 'CC_Base_{S}_Clavicle', '{Side}Arm': 'CC_Base_{S}_Upperarm',
      '{Side}ForeArm': 'CC_Base_{S}_Forearm', '{Side}Hand': 'CC_Base_{S}_Hand',
      '{Side}UpLeg': 'CC_Base_{S}_Thigh', '{Side}Leg': 'CC_Base_{S}_Calf',
      '{Side}Foot': 'CC_Base_{S}_Foot', '{Side}ToeBase': 'CC_Base_{S}_ToeBase',
    },
    'L', 'R',
  ),
};

/** VRM (J_Bip): humanoid part names; Chest→Spine1, UpperChest→Spine2 per the v2 table. */
const VRM: Names = {
  Hips: 'J_Bip_C_Hips', Spine: 'J_Bip_C_Spine', Spine1: 'J_Bip_C_Chest', Spine2: 'J_Bip_C_UpperChest',
  Neck: 'J_Bip_C_Neck', Head: 'J_Bip_C_Head',
  ...bothSides(
    {
      '{Side}Shoulder': 'J_Bip_{S}_Shoulder', '{Side}Arm': 'J_Bip_{S}_UpperArm',
      '{Side}ForeArm': 'J_Bip_{S}_LowerArm', '{Side}Hand': 'J_Bip_{S}_Hand',
      '{Side}UpLeg': 'J_Bip_{S}_UpperLeg', '{Side}Leg': 'J_Bip_{S}_LowerLeg',
      '{Side}Foot': 'J_Bip_{S}_Foot', '{Side}ToeBase': 'J_Bip_{S}_ToeBase',
    },
    'L', 'R',
  ),
  // VRM 'Little' is BBSA 'Pinky'.
  ...fingerTable((side, finger, seg) => `J_Bip_${side === 'Left' ? 'L' : 'R'}_${finger === 'Pinky' ? 'Little' : finger}${seg}`),
};

/** CMU BVH (classic lowercase convention; 2 finger segments, no toes). */
const CMU_BVH: Names = {
  Hips: 'hip', Spine: 'abdomen', Spine1: 'chest', Neck: 'neck', Head: 'head',
  ...bothSides(
    {
      '{Side}Shoulder': '{S}Collar', '{Side}Arm': '{S}Shldr', '{Side}ForeArm': '{S}ForeArm', '{Side}Hand': '{S}Hand',
      '{Side}UpLeg': '{S}Thigh', '{Side}Leg': '{S}Shin', '{Side}Foot': '{S}Foot',
    },
    'l', 'r',
  ),
  ...Object.fromEntries(
    SIDES.flatMap(([side]) =>
      FINGERS.flatMap((finger) =>
        [1, 2].map((seg) => [
          `${side}Hand${finger}${seg}`,
          `${side === 'Left' ? 'l' : 'r'}${finger === 'Middle' ? 'Mid' : finger}${seg}`,
        ]),
      ),
    ),
  ),
};

/** CMU MotionBuilder BVH: Mixamo-like names with a shifted spine chain. */
const CMU_MOTIONBUILDER: Names = {
  Hips: 'Hips', Spine: 'LowerBack', Spine1: 'Spine', Spine2: 'Spine1', Neck: 'Neck', Head: 'Head',
  ...bothSides(
    {
      '{Side}Shoulder': '{S}Shoulder', '{Side}Arm': '{S}Arm', '{Side}ForeArm': '{S}ForeArm', '{Side}Hand': '{S}Hand',
      '{Side}UpLeg': '{S}UpLeg', '{Side}Leg': '{S}Leg', '{Side}Foot': '{S}Foot', '{Side}ToeBase': '{S}ToeBase',
    },
    'Left', 'Right',
  ),
  LeftHandThumb1: 'LThumb', RightHandThumb1: 'RThumb',
  LeftHandIndex1: 'LeftHandIndex1', RightHandIndex1: 'RightHandIndex1',
};

/** Ready Player Me — platform DEPRECATED; names kept as knowledge only. */
const READY_PLAYER_ME: Names = {
  Hips: 'Hips', Spine: 'Spine', Spine1: 'Spine1', Spine2: 'Spine2', Neck: 'Neck', Head: 'Head',
  LeftEye: 'LeftEye', RightEye: 'RightEye',
  ...bothSides(
    {
      '{Side}Shoulder': '{S}Shoulder', '{Side}Arm': '{S}Arm', '{Side}ForeArm': '{S}ForeArm', '{Side}Hand': '{S}Hand',
      '{Side}UpLeg': '{S}UpLeg', '{Side}Leg': '{S}Leg', '{Side}Foot': '{S}Foot', '{Side}ToeBase': '{S}ToeBase',
    },
    'Left', 'Right',
  ),
  ...fingerTable((side, finger, seg) => `${side}Hand${finger}${seg}`),
};

/**
 * OpenSim/SL Bento — LIVE source platform since ADR-191 (the legacy-content
 * clip rail; SL_ANIM_DECODE_INVESTIGATION_2026-08-08).
 *
 * Spine2 is `mChest`, NOT Bento's `mSpine4` — a measured call, not a
 * simplification: the full canonical skeleton carries BOTH names
 * (mTorso → mSpine3 → mSpine4 → mChest), so they compete for the one Spine2
 * slot, and the 2026-08-08 census has mChest animated in ~2,659/2,707 prod
 * clips vs mSpine4 in ~16. Mapping mSpine4 would land the chest rotation of
 * nearly every clip on an unmapped bone — dead torsos. mSpine1/2/4 stay
 * unmapped by design (zero-sum interposed Bento helpers). `mFoot*` is also
 * deliberately absent: Foot maps from mAnkle (the deforming joint), and the
 * mFootLeft/Right segment between ankle and toe has no ontology slot.
 */
const OPENSIM: Names = {
  Hips: 'mPelvis', Spine: 'mTorso', Spine1: 'mSpine3', Spine2: 'mChest', Neck: 'mNeck', Head: 'mHead',
  LeftEye: 'mEyeLeft', RightEye: 'mEyeRight',
  ...bothSides(
    {
      '{Side}Shoulder': 'mCollar{S}', '{Side}Arm': 'mShoulder{S}', '{Side}ForeArm': 'mElbow{S}', '{Side}Hand': 'mWrist{S}',
      '{Side}UpLeg': 'mHip{S}', '{Side}Leg': 'mKnee{S}', '{Side}Foot': 'mAnkle{S}', '{Side}ToeBase': 'mToe{S}',
    },
    'Left', 'Right',
  ),
  ...fingerTable((side, finger, seg) => `mHand${finger}${seg}${side}`),
};

/** v2's fuzzy-fallback alias strings (initializeStandardBones), as plain synonyms. */
const FUZZY: Record<string, readonly string[]> = {
  Hips: ['hip', 'pelvis'],
  Spine: ['waist'],
  Spine1: ['Spine_1'],
  Spine2: ['Spine_2', 'Chest', 'UpperChest', 'Upper_Chest'],
};

/** Observed real-platform variants, provenance = the specimen that taught us. */
const CORPUS: Record<string, ReadonlyArray<{ name: string; source: string }>> = {
  HeadTop_End: [{ name: 'head_end', source: 'corpus:meshy-biped' }],
};

const PLATFORMS: ReadonlyArray<[string, Names]> = [
  ['v2:mixamo', MIXAMO],
  ['v2:meshy3d', MESHY3D],
  ['v2:tripoai', TRIPOAI],
  ['v2:character_creator', CHARACTER_CREATOR],
  ['v2:vrm', VRM],
  ['v2:cmu_bvh', CMU_BVH],
  ['v2:cmu_motionbuilder', CMU_MOTIONBUILDER],
  ['v2:ready_player_me', READY_PLAYER_ME],
  ['v2:opensim', OPENSIM],
  ['tripo-preset:fingers', TRIPO_PRESET_FINGERS],
  ['ue:mannequin', UNREAL_MANNEQUIN],
  ['unity:humanoid', UNITY_HUMANOID],
];

/**
 * Authored leg frames for the VRM/VRoid family (2026-07-30) — the fix for
 * Bobby's sideways walk (probe: app/src/import/bobby-root-motion-probe).
 *
 * WHY THESE EXIST, precisely. The measured twist ladder prefers a GRANDCHILD
 * direction ('two-directions', high confidence) over the declared canonical
 * axis. For a leg in T-pose the hip→knee→ankle chain is nearly COLLINEAR, so
 * that grandchild's perpendicular remainder is a fraction of a degree —
 * VRoid's knee pre-bend measures 0.35°. `projectedSecondary`'s floor asks
 * only whether that remainder clears MEASUREMENT NOISE, which it does, so a
 * 0.35° lever arm becomes the twist reference for the whole femur. The
 * resulting roll error lands about the femur's own long axis, and because the
 * femur hangs DOWNWARD that roll is very nearly a world YAW: it rotates the
 * leg's swing plane off sagittal instead of tilting the body. Measured
 * consequence — femur coronal swing 8.1° → 24.7°, and each foot crossing the
 * centreline. (The Tripo android escapes only by accident: its knee is dead
 * straight, so the grandchild FAILS the floor and the well-conditioned
 * canonical tier runs instead.)
 *
 * These entries are declarations, so the authored tier — which outranks every
 * measured tier (ADR-010 §11, the hand wins) — supplies a well-conditioned
 * secondary and the degenerate grandchild is never consulted.
 *
 * MEASURED, not guessed: `app/src/import/vrm-leg-frame-measure` over five
 * VRoid rigs (Bobby + four wardrobe exports) found these frames constant to
 * ±0.04°, so they are a family property rather than a per-file one. VRoid's
 * femur points down its local −Y (Mixamo and Tripo point down +Y — the
 * antiparallel primaries behind the 179.7° bind prior); local +Z is forward
 * on all three, which is why the secondary is the canonical FWD.
 *
 * The general cure — teaching the twist ladder to reject a geometrically
 * ill-conditioned witness rather than merely a noisy one — is the ADR-level
 * change deliberately NOT made here (Allen, 2026-07-30: VRM family first,
 * ADR after).
 */
const VRM_LEG_FRAMES: ReadonlyArray<{ ontologyId: string }> = [
  { ontologyId: 'LeftUpLeg' },
  { ontologyId: 'RightUpLeg' },
  { ontologyId: 'LeftLeg' },
  { ontologyId: 'RightLeg' },
];

/**
 * Authored spine frames for the VRM family (2026-08-03) — the femur cure's
 * torso cousin, and the cure for the WAIST PINCH (Allen's field find: a
 * VRoid's torso wrings at the bra line mid-stride). MEASURED first
 * (vrm-waist-probe): J_Bip_C_Chest (Spine1) and J_Bip_C_UpperChest (Spine2)
 * were being driven to ~179° flipped locals — two consecutive half-turns
 * that CANCEL, so the head and arms read fine while the flesh between Spine
 * and Chest wears a hidden 180° twist. Same disease class as the femur yaw:
 * near-collinear spine children leave the twist ladder an ill-conditioned
 * witness, and the resolved frame lands flipped.
 *
 * MEASURED, not guessed: five VRM1 rigs (39, rabbit girl, tallfoxlady,
 * smallmale, Pinkie) carry BIT-IDENTICAL spine frames — primary ≈ local +Y
 * (toward the child, within 7°), anatomical forward ≈ local +Z (within 19°
 * on UpperChest, but the deviation lies wholly in the Y–Z plane, so it
 * orthogonalizes out of the twist reference; the idealized pair below is
 * twist-equivalent to the measurement). A family property, like the legs.
 *
 * KNOWN BOUNDARY: VRM 0.x measures forward = local −Z (anterior is −Z on
 * that generation). A VRoid-Studio VRM0 export classified into this family
 * would take these frames with the wrong secondary sign — the same latent
 * boundary the LEG frames already carry, resolved when the ADR-013
 * humanoid-map/generation work lands. Today's corpus retargets VRM1.
 */
const VRM_SPINE_FRAMES: ReadonlyArray<{ ontologyId: string }> = [
  { ontologyId: 'Spine1' },
  { ontologyId: 'Spine2' },
];

/**
 * Authored limb frames for the current-gen Tripo family (2026-07-31) — the
 * fix for the A-pose arm flip (probe: app/src/import/apose-arm-probe;
 * diagnosis in that probe's header).
 *
 * WHY THESE EXIST, precisely. On every measured Tripo rig the mirror-pair
 * twist witnesses contradict (authoring noise on near-straight generated
 * limbs — the corroboration gate demotes them, CORRECTLY), which drops the
 * twist to the canonical-secondary tier. That tier computes the declared
 * axis by assuming the bind was authored in the declared world convention —
 * the T-pose idealization — so on an A-pose rig its secondary is wrong by
 * the full abduction angle: upper arms resolved with 48–78° residuals and
 * the forearm forward-swing landed up to 97° off the source. The visible
 * symptom is the crossed/flipped arms on every A-pose generation.
 *
 * MEASURED, not guessed: `app/src/import/tripo-frame-measure` over eight
 * rigs spanning BOTH rest poses (golden android, frog, water fairy,
 * fashion character = T/soft-A; desert warrior, stylized female, fem
 * archaologist, fox warrior = A). Every limb bone points down its local +Y
 * at ±0.04° regardless of pose — Tripo carries the joint frame WITH the
 * limb — and anatomical FORWARD is local +Z on the whole limb set
 * (shoulders ±1.5°, upper arms ±4–13°, legs ±1°; forearms are the noisiest
 * authoring at ±25°, accepted as consensus because every alternative tier
 * is worse there).
 *
 * The secondary semantic is FORWARD deliberately, overriding the arms'
 * ontology-declared UP: the twist reference must survive the T↔A
 * correspondence — a rotation about the facing axis — and forward is that
 * rotation's invariant where up is not. Pinning arm twist to up-at-bind on
 * each side separately is exactly the cross-pose twist error the A-pose
 * flip reduces to (the legs never had the disease BECAUSE their declared
 * semantic is forward). Both sides of the pair must speak the same
 * semantic, which is why MIXAMO_LIMB_FRAMES exists below.
 *
 * The CLAVICLES are authored too (±1.5° family constants): the clavicle
 * class is declared child-direction-uninformative, so without an authored
 * frame it rides the declared-convention tier — and a clavicle TWIST error
 * does not move the clavicle's own segment, it rotates the entire arm's
 * swing plane. Hands stay absent: they carry graft-authored frames when it
 * matters.
 */
const TRIPO_LIMB_FRAMES: ReadonlyArray<{ ontologyId: string }> = [
  { ontologyId: 'LeftShoulder' },
  { ontologyId: 'RightShoulder' },
  { ontologyId: 'LeftArm' },
  { ontologyId: 'RightArm' },
  { ontologyId: 'LeftForeArm' },
  { ontologyId: 'RightForeArm' },
  { ontologyId: 'LeftUpLeg' },
  { ontologyId: 'RightUpLeg' },
  { ontologyId: 'LeftLeg' },
  { ontologyId: 'RightLeg' },
];

/**
 * Authored arm-chain frames for the Mixamo family (2026-07-31) — the SOURCE
 * side of the same fix. The twist alignment pairs the two sides' secondary
 * axes, so they must name the same semantic: an authored forward frame on a
 * Tripo target paired against Mixamo's canonical UP is a built-in ~90°
 * twist (measured — the first authoring attempt did exactly that). Measured
 * by the same harness on the Alpha_Joints skeleton: limb bones point down
 * local +Y; forward is sided local ∓X on the whole arm chain (left −X,
 * right +X), exact to display precision. Legs are deliberately absent:
 * Mixamo's canonical leg secondary is already the forward semantic, so
 * authoring would add surface without changing behaviour.
 */
const MIXAMO_ARM_FRAMES: ReadonlyArray<{ ontologyId: string; sx: number }> = [
  { ontologyId: 'LeftShoulder', sx: -1 },
  { ontologyId: 'RightShoulder', sx: 1 },
  { ontologyId: 'LeftArm', sx: -1 },
  { ontologyId: 'RightArm', sx: 1 },
  { ontologyId: 'LeftForeArm', sx: -1 },
  { ontologyId: 'RightForeArm', sx: 1 },
];

/** The default priors bundle — plain data, validated by parsePriorsBundle at use. */
export function v2PriorsData(): PriorsBundle {
  const byId = new Map<string, Synonym[]>();
  const add = (ontologyId: string, name: string, source: string): void => {
    const list = byId.get(ontologyId) ?? [];
    list.push({ name, source });
    byId.set(ontologyId, list);
  };
  for (const [source, table] of PLATFORMS) {
    for (const [id, name] of Object.entries(table)) add(id, name, source);
  }
  for (const [id, names] of Object.entries(FUZZY)) {
    for (const name of names) add(id, name, 'v2:fuzzy');
  }
  for (const [id, synonyms] of Object.entries(CORPUS)) {
    for (const s of synonyms) add(id, s.name, s.source);
  }
  return {
    version: '1.0.0',
    ontologyVersion: '3.1.0',
    lexicon: [...byId.entries()].map(([ontologyId, synonyms]) => ({ ontologyId, synonyms })),
    jointFrames: [
      // Two measured Meshy generations: straight spine geometry is an
      // ambiguous roll witness. Stable local +Y/+Z removes cancelling
      // half-turns in the Mixamo walk. Other family pairings stay unchanged.
      ...['Spine1', 'Spine2'].map(ontologyId => ({
        family: 'meshy', ontologyId,
        appliesTo: 'target' as const,
        pairedFamilies: ['mixamo'],
        localPrimary: { x: 0, y: 1, z: 0 },
        localSecondary: { x: 0, y: 0, z: 1 },
        secondaryMeans: 'anterior' as const,
        provenance: ['authored:corpus:meshy-biped-and-pharaoh-spine-2026-09-12'],
      })),
      // SL's canonical skeleton has identity rest rotations, +X forward,
      // and arms along sided Y. Match the Tripo/Mixamo FORWARD secondary:
      // falling back to ontology UP adds a quarter-turn of arm twist.
      // Field accepted on Prince, Thea (A-pose), Amari, Bart and Bambi.
      ...MIXAMO_ARM_FRAMES.map(({ ontologyId }) => ({
        family: 'sl',
        ontologyId,
        pairedFamilies: ['sl', 'tripo', 'mixamo'],
        localPrimary: { x: 0, y: ontologyId.startsWith('Left') ? 1 : -1, z: 0 },
        localSecondary: { x: 1, y: 0, z: 0 },
        secondaryMeans: 'anterior' as const,
        provenance: ['authored:sl-canonical-forward:field-2026-09-12'],
      })),
      ...VRM_LEG_FRAMES.map(({ ontologyId }) => ({
        family: 'vrm',
        ontologyId,
        // VRoid's leg bones point down their local −Y; local +Z is anterior.
        localPrimary: { x: 0, y: -1, z: 0 },
        localSecondary: { x: 0, y: 0, z: 1 },
        secondaryMeans: 'anterior' as const,
        provenance: ['authored:corpus:vroid-5-rig-measurement'],
      })),
      ...VRM_SPINE_FRAMES.map(({ ontologyId }) => ({
        family: 'vrm',
        ontologyId,
        // VRoid's spine bones point UP their local +Y; local +Z is anterior.
        localPrimary: { x: 0, y: 1, z: 0 },
        localSecondary: { x: 0, y: 0, z: 1 },
        secondaryMeans: 'anterior' as const,
        provenance: ['authored:corpus:vroid-5-rig-spine-measurement-2026-08-03'],
      })),
      ...TRIPO_LIMB_FRAMES.map(({ ontologyId }) => ({
        family: 'tripo',
        ontologyId,
        // Tripo limb bones point down their local +Y; local +Z is anatomical
        // forward — the pose-invariant twist reference (see header).
        localPrimary: { x: 0, y: 1, z: 0 },
        localSecondary: { x: 0, y: 0, z: 1 },
        secondaryMeans: 'anterior' as const,
        provenance: ['authored:corpus:tripo-8-rig-measurement'],
      })),
      ...MIXAMO_ARM_FRAMES.map(({ ontologyId, sx }) => ({
        family: 'mixamo',
        ontologyId,
        // Mixamo arm bones point down local +Y; forward is sided local ∓X.
        localPrimary: { x: 0, y: 1, z: 0 },
        localSecondary: { x: sx, y: 0, z: 0 },
        secondaryMeans: 'anterior' as const,
        provenance: ['authored:corpus:mixamo-alpha-joints-measurement'],
      })),
    ],
    // ADR-028 E1: the Tripo elbow RULE — axes are measured per character at
    // graft (a family-constant axis was falsified by measurement: Tripo rolls
    // bones about their long axis within the family). Elbows only until the
    // elbow is field-judged; the knee is one entry here when D3's second
    // profile lands (ADR-028 §10).
    // ADR-029: Tripo paints the buttock and crotch to `Waist` (BBSA Spine)
    // with the root stack (Hip → Pelvis → Waist) AT hip-joint height —
    // measured on six bodies 2026-08-22: 30–50% of Waist's weight mass lies
    // below the hip joints, Pelvis and Hip hold none, and a 60° bow loses
    // 11–44% of the buttock's posterior depth. Flesh below the Spine1 joint
    // (0.18–0.25 thigh lengths above the hips — where a human iliac crest
    // sits) belongs to the bone the thighs hang from. Declared in ontology
    // ids; resolved per rig.
    // SL clips are poses OF SL's identity rest, so an SL mesh plays identity
    // as its own bind. Absolute transport instead imposed the SL skeleton's
    // rest geometry on the axial chain: an identity mPelvis landed 5.1° off
    // the Tripo Hip's rest and the neck 16.8° back (Avery anchor; the Eternal
    // wait poses, World's field read 2026-09-11). Axial chain only — the
    // limbs stay on the field-accepted absolute path (their prefixes cancel
    // the anchor, so their world pose is unchanged). The Head's articulated
    // children (Jaw, eyes) are anchored too, or a keyed eye would cancel the
    // head's correction and counter-rotate against the face (Codex P2, #179:
    // 18 OAR clips key both eyes; no Tripo target maps them yet).
    restAnchors: [
      {
        family: 'sl',
        pairedFamilies: ['tripo'],
        ontologyIds: ['Hips', 'Spine', 'Spine1', 'Spine2', 'Neck', 'Head', 'Jaw', 'LeftEye', 'RightEye'],
        provenance: ['authored:sl-rest-relative-axial:2026-09-22'],
      },
    ],
    pelvisOwnership: [
      {
        family: 'tripo',
        station: 'Spine1',
        donors: ['Spine', 'Spine1', 'Spine2'],
        seamFraction: 0.25,
        provenance: ['authored:adr-029-pelvis-ownership', 'measured:corpus:pelvis-census-2026-08-22'],
      },
    ],
    // The knee is D3's ruled second declaration (ADR-028 §10, 2026-08-21) —
    // same machinery, opposite flexion: `anterior` is the canonical facing
    // frame (+Z = the character's facing); elbows bend toward it, knees away.
    // Knee disease measured before the cure, per §9: 4–45% joint-slab
    // collapse at 135° across the seven-specimen sweep.
    hingeTemplates: [
      {
        family: 'tripo',
        hinges: (['R', 'L'] as const).flatMap((s) => [
          {
            driver: `${s}_Forearm`,
            end: `${s}_Hand`,
            helper: `${s}_ElbowHalf_corr`,
            factor: 0.5,
            anterior: { x: 0, y: 0, z: 1 },
            bandFraction: 0.15,
            // E3 crease morphs (ADR-028 D5, ramp-as-metadata). The declaration
            // is a NAME and a RAMP; the shape is derived per character at
            // bake (D2) from the delta-mush-vs-LBS diff, never shipped here —
            // a family template cannot carry a character's millimetres.
            //
            // The two ramps are deliberately DISJOINT at their peaks: stage 75
            // stands alone at 75 degrees and stage 135 alone at 135. Daz ships
            // both live at once, which makes deriving the second a linear
            // solve against the first; we author the ramps, so we decline
            // that and each stage derives independently against raw LBS.
            morphs: [
              {
                name: `${s}_ElbowCrease75_corr`,
                ramp: [
                  { thetaDeg: 0, value: 0 },
                  { thetaDeg: 75, value: 1 },
                  { thetaDeg: 135, value: 0 },
                ],
                provenance: ['authored:ADR-028-E3-D5'],
              },
              // THE 135-DEGREE STAGE IS NOT DECLARED YET (Allen's ruling,
              // 2026-08-26: "ship 75 now"). Measured, it makes deep bends
              // WORSE on nearly every specimen — girth recovery -0.16 to -1.07
              // across Sergio, Hamus, Brenda, Kara, Jay and the Atlantians —
              // while this 75-degree stage measures 0.65 to 1.01 on the
              // healthy ones. Shipping both would ship a stage known to be
              // wrong.
              //
              // The irony is recorded because it is the next task: the
              // COLLAPSE LIVES AT 135 (girth loss ~6% at 75 against 12-28% at
              // 135), so the stage that works is correcting little and the
              // stage where the damage is does not work. What reads well at
              // deep bends today is E1's half-rotation and E2's leaves, not
              // the crease. The declaration returns when the deep-flexion
              // derivation earns it.
            ],
            // E2 muscle leaves (ADR-028 §2): the bicep belly at ~60% of the
            // humerus and the forearm flexor mass in the proximal third —
            // Figgins/Parks/MetaHuman's convergent placement. Ramps wake past
            // ~60° (the survey's visible-growth threshold); directions are
            // anatomical and resolved per character at graft.
            // RAMP UNITS = fractions of the belly's MEASURED RADIUS (§6:
            // amplitude scales with girth; the Document run measures each
            // leaf's ring radius from the bind paint and makes it the scale).
            // Calibrated 2026-08-22 so the corpus median keeps the
            // field-confirmed swell ("arms are def swelling on bend"): the
            // prior length-fraction amplitudes (0.08 / 0.045 at 135°) times
            // the measured median segment/radius (bicep 2.76, forearm 3.87
            // over Sergio/Kara/Hamus/Brenda/Jett) — a slim arm now swells
            // fewer millimetres, a thick one more.
            leaves: [
              {
                name: `${s}_BicepLeaf_corr`,
                segment: 'proximal' as const,
                along: 0.6,
                direction: { anterior: 0.9, proximal: 0.44, lateral: 0 },
                ramp: [
                  { thetaDeg: 0, value: 0 },
                  { thetaDeg: 60, value: 0.022 },
                  { thetaDeg: 135, value: 0.22 },
                ],
                radiusFraction: 0.28,
                radiusPrior: 0.36, // 1 / 2.76 — the corpus median upper-arm ring radius per humerus length
                weightShare: 0.5,
                provenance: ['authored:adr-028-e2', 'measured:corpus:leaf-girth-calibration-2026-08-22'],
              },
              {
                name: `${s}_ForearmFlexorLeaf_corr`,
                segment: 'distal' as const,
                along: 0.3,
                direction: { anterior: 1, proximal: 0.2, lateral: 0 },
                ramp: [
                  { thetaDeg: 0, value: 0 },
                  { thetaDeg: 60, value: 0.023 },
                  { thetaDeg: 135, value: 0.175 },
                ],
                radiusFraction: 0.25,
                radiusPrior: 0.26, // 1 / 3.87 — the corpus median forearm ring radius per forearm length
                weightShare: 0.4,
                provenance: ['authored:adr-028-e2'],
              },
            ],
            provenance: ['authored:adr-028-e1'],
          },
          {
            driver: `${s}_Calf`,
            end: `${s}_Foot`,
            helper: `${s}_KneeHalf_corr`,
            factor: 0.5,
            anterior: { x: 0, y: 0, z: -1 },
            bandFraction: 0.1,
            provenance: ['authored:adr-028-d3-knee'],
          },
        ]),
      },
    ],
  };
}
